/**
 * Activity Tab — unified timeline of EVERY change on this service job.
 *
 * Sources merged here (chronologically sorted):
 *   1. `lead.activities`     — operational events written by the backend
 *      (invoice, payment, spares, ops_status_change, mechanic_assigned).
 *   2. `lead.status_history` — lifecycle transitions written when status
 *      changes (BOOKED → ASSIGNED → IN_PROGRESS → COMPLETED, reschedules,
 *      cancellations, mechanic rejections). The backend currently writes
 *      these to a SEPARATE array from `activities`, so the Activity tab
 *      used to silently miss them.
 *   3. `lead.actual_start_at` / `lead.actual_end_at` — mechanic clock-in
 *      and clock-out timestamps. Synthesised into IN_PROGRESS / COMPLETED
 *      rows only when status_history doesn't already carry them.
 *   4. `lead.created_at` — anchor "Lead Created" row for older leads that
 *      were created before history-tracking shipped.
 *
 * Dedup rules: if a status transition exists in BOTH `activities` (as
 * `ops_status_change`) and in `status_history`, keep only the activities
 * entry (richer fields). Same for `mechanic_assigned`.
 */
import React from "react";
import { formatCurrency } from "../../../utils/currency";
import { formatDateTime } from "../../../utils/datetime";

const ICONS = {
  status_change:                 "🔄",
  ops_status_change:             "🔄",
  mechanic_assigned:             "🔧",
  spare_part_added:              "➕",
  spare_part_updated:            "✏️",
  spare_part_deleted:            "❌",
  additional_payment_link_sent:  "💳",
  payment_status_override:       "⚠️",
  invoice_generated:             "📄",
  invoice_sent:                  "📨",
  // Lifecycle-only types (synthesised from status_history / clock-in-out)
  lead_created:                  "📅",
  booking_confirmed:             "📅",
  slot_confirmed:                "✅",
  mechanic_rejected:             "⚠️",
  service_started:               "▶️",
  service_completed:             "✅",
  rescheduled:                   "🔁",
  cancelled:                     "🚫",
  refund_requested:              "💸",
};

const TYPE_LABEL = {
  status_change:                 "Status changed",
  ops_status_change:             "Status changed",
  mechanic_assigned:             "Mechanic assigned",
  spare_part_added:              "Spare part added",
  spare_part_updated:            "Spare part updated",
  spare_part_deleted:            "Spare part removed",
  additional_payment_link_sent:  "Payment link created",
  payment_status_override:       "Payment status override",
  invoice_generated:             "Invoice generated",
  invoice_sent:                  "Invoice sent",
  lead_created:                  "Lead created",
  booking_confirmed:             "Booking confirmed",
  slot_confirmed:                "Slot confirmed",
  mechanic_rejected:             "Mechanic rejected",
  service_started:               "Service started",
  service_completed:             "Service completed",
  rescheduled:                   "Rescheduled",
  cancelled:                     "Cancelled",
  refund_requested:              "Refund requested",
};

// Map raw `status_history.to` values into the synthesised activity types
// above. Anything not listed falls back to `status_change`.
const STATUS_TO_TYPE = {
  NEW:                     "lead_created",
  PAYMENT_LINK_SENT:       "additional_payment_link_sent",
  BOOKED:                  "booking_confirmed",
  CONFIRMED:               "slot_confirmed",
  ASSIGNED:                "mechanic_assigned",
  MECHANIC_REJECTED:       "mechanic_rejected",
  IN_PROGRESS:             "service_started",
  COMPLETED:               "service_completed",
  RESCHEDULED_BY_CUSTOMER: "rescheduled",
  RESCHEDULED_BY_WD:       "rescheduled",
  CANCELLED_BY_CUSTOMER:   "cancelled",
  CANCELLED_BY_WD:         "cancelled",
  REFUND_REQUESTED:        "refund_requested",
};

function buildEvents(lead) {
  const events = [];

  // 1) Native `activities` array — invoice, payment, spare, etc.
  (lead?.activities || []).forEach((a, i) => {
    events.push({ ...a, _src: "activities", _key: a.id || `act-${i}` });
  });

  // 2) Lifecycle transitions from `status_history`
  const history = Array.isArray(lead?.status_history) ? lead.status_history : [];
  // Dedup: skip status_history entries that already appear in `activities`
  // (same `to` status + same `at` timestamp within 5 sec).
  const activitiesByStatus = new Set(
    (lead?.activities || [])
      .filter(a => a.type === "status_change" || a.type === "ops_status_change" || a.type === "mechanic_assigned")
      .map(a => `${a.to || ""}|${(a.at || "").slice(0, 16)}`),
  );
  history.forEach((h, i) => {
    const status = h.to || h.status;
    if (!status) return;
    const sig = `${status}|${(h.at || "").slice(0, 16)}`;
    if (activitiesByStatus.has(sig)) return;
    events.push({
      _src:   "status_history",
      _key:   `hist-${i}`,
      type:   STATUS_TO_TYPE[status] || "status_change",
      from:   h.from,
      to:     status,
      by:     h.by_name || h.by || null,
      at:     h.at || h.timestamp || null,
      note:   h.note || null,
    });
  });

  // 3) Mechanic clock-in / clock-out — only if not already covered above
  const haveStarted = events.some(e => e.type === "service_started"
                                    || (e.type === "status_change" && e.to === "IN_PROGRESS")
                                    || (e.type === "ops_status_change" && e.to === "IN_PROGRESS"));
  const haveCompleted = events.some(e => e.type === "service_completed"
                                      || (e.type === "status_change" && e.to === "COMPLETED")
                                      || (e.type === "ops_status_change" && e.to === "COMPLETED"));
  if (lead?.actual_start_at && !haveStarted) {
    events.push({
      _src: "synth", _key: "clock-in",
      type: "service_started",
      by:   lead.assigned_mechanic_name || null,
      at:   lead.actual_start_at,
      note: "Mechanic clocked in",
    });
  }
  if (lead?.actual_end_at && !haveCompleted) {
    events.push({
      _src: "synth", _key: "clock-out",
      type: "service_completed",
      by:   lead.assigned_mechanic_name || null,
      at:   lead.actual_end_at,
      note: "Mechanic clocked out",
    });
  }

  // 4) Lead-created anchor (only if status_history didn't already include it)
  const haveCreated = events.some(e => e.type === "lead_created");
  if (lead?.created_at && !haveCreated) {
    events.push({
      _src: "synth", _key: "created",
      type: "lead_created",
      at:   lead.created_at,
      by:   null,
      note: null,
    });
  }

  // Sort DESCENDING (newest first) so the most recent event is at the top —
  // matches the original ActivityTab's `.reverse()` UX.
  events.sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
  return events;
}

export default function ActivityTab({ lead, user }) {
  const events = buildEvents(lead);

  if (events.length === 0) {
    return (
      <div className="p-12 text-center text-slate-400">
        <div className="text-sm italic">No activity recorded yet.</div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="activity-timeline">
      <ol className="relative border-l border-slate-200 ml-3">
        {events.map((a) => (
          <li key={a._key} className="ml-6 pb-5" data-testid={`activity-${a._key}`}>
            <span className="absolute -left-3 grid place-items-center w-6 h-6 rounded-full bg-white border text-sm">
              {ICONS[a.type] || "•"}
            </span>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-semibold text-slate-900 text-sm">
                {TYPE_LABEL[a.type] || a.type}
              </span>
              {a.from && a.to && (
                <span className="text-xs text-slate-500">
                  {a.from} → <b>{a.to}</b>
                </span>
              )}
              {a.to && !a.from && a.type === "mechanic_assigned" && (
                // Shown when transition went directly to ASSIGNED — surface
                // the mechanic's name if denormalised on the activity row.
                a.mechanic_name && (
                  <span className="text-xs text-slate-500">
                    → <b>{a.mechanic_name}</b>
                  </span>
                )
              )}
              {a.part && (
                <span className="text-xs text-slate-500">
                  · {a.part} {a.qty ? `× ${a.qty}` : ""}{" "}
                  {a.total ? `· ${formatCurrency(a.total, user)}` : ""}
                </span>
              )}
              {a.amount && (
                <span className="text-xs text-slate-500">
                  · {formatCurrency(a.amount, user)}
                </span>
              )}
              {a.invoice_number && (
                <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">
                  {a.invoice_number}
                </code>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {a.by && <span>by <span className="font-medium text-slate-700">{a.by}</span></span>}
              {a.at && (
                <span className="ml-2 text-slate-400">
                  {formatDateTime(a.at)}
                </span>
              )}
              {a._src === "status_history" && (
                <span className="ml-2 text-[10px] text-slate-300 italic">(lifecycle)</span>
              )}
              {a._src === "synth" && (
                <span className="ml-2 text-[10px] text-slate-300 italic">(derived)</span>
              )}
            </div>
            {a.note && (
              <div className="text-xs text-slate-600 italic mt-1 bg-slate-50 p-2 rounded border-l-2 border-slate-300">
                {a.note}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
